import { existe, t } from '../i18n/idioma';

/**
 * O cartão pelo Checkout Pro do Mercado Pago (techspec-checkout-pro), na aba Ouro e nos Founder Packs.
 *
 * O jogo não mostra formulário de cartão: o servidor cria a order e devolve a página do Mercado Pago, que abre numa
 * janela. O jogador paga lá (cartão, cartão salvo na conta MP), e o jogo só acompanha o pedido pela consulta. Número,
 * CVV, CPF e o 3DS nunca passam por aqui.
 */

const DOMINIOS_MP = /(^|\.)(mercadopago\.com|mercadopago\.com\.br|mercadolibre\.com|mercadolivre\.com\.br)$/;
/** No simulado (local) e no mock, a "página do Mercado Pago" é a de retorno do próprio site. */
const PAGINA_LOCAL = '/pagamento-retorno.html?';

/**
 * A página do checkout, conferida de novo no cliente: só HTTPS dos domínios do Mercado Pago (o servidor já conferiu),
 * ou a página local de retorno. Qualquer outra coisa não abre.
 */
export function urlDoCheckout(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith(PAGINA_LOCAL) && url.length <= 256) return url;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && DOMINIOS_MP.test(u.hostname) ? u.href : null;
  } catch {
    return null;
  }
}

/**
 * Abre a janela já no clique: o navegador só deixa abrir janela em resposta direta ao clique, e o endereço só chega
 * depois que o servidor cria o pedido. A janela nasce sem acesso a esta página (<c>opener</c> nulo).
 * Bloqueada, devolve nulo e a tela oferece o link.
 */
export function abrirJanelaCheckout(): Window | null {
  const w = 520;
  const h = 760;
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - w) / 2));
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - h) / 2));
  const janela = window.open('about:blank', 'uoidle-checkout', `popup=yes,width=${w},height=${h},left=${left},top=${top}`);
  if (!janela) return null;
  try {
    janela.opener = null;
    janela.document.title = t('pag.checkout.abrindo');
    janela.document.body.style.cssText = 'margin:0;display:grid;place-items:center;height:100vh;background:#0f0c09;color:#d9b26a;font:16px Georgia,serif';
    janela.document.body.textContent = t('pag.checkout.abrindo');
  } catch { /* a janela já existe com outra origem: só o endereço muda */ }
  return janela;
}

/** Leva a janela aberta no clique para a página do Mercado Pago. Falso: não havia janela (bloqueada ou fechada). */
export function levarAoCheckout(janela: Window | null, url: string | null | undefined): boolean {
  const seguro = urlDoCheckout(url);
  if (!seguro) {
    fecharJanela(janela);
    return false;
  }
  if (!janela || janela.closed) return false;
  try {
    janela.location.replace(seguro);
    janela.focus();
    return true;
  } catch {
    return false;
  }
}

export function fecharJanela(janela: Window | null): void {
  try {
    if (janela && !janela.closed) janela.close();
  } catch { /* nada a fazer */ }
}

export const novaChaveCartao = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });

export const motivoDaRecusa = (detalhe: string | null | undefined) =>
  detalhe && existe(`pag.recusa.${detalhe}`) ? t(`pag.recusa.${detalhe}`) : t('pag.recusa.generico');

/**
 * O link para abrir (de novo) a página do Mercado Pago: aba nova, sem acesso a esta página. É o caminho quando o
 * navegador bloqueou a janela ou o jogador a fechou.
 */
export function LinkDoCheckout({ url, classe, texto }: { url: string | null | undefined; classe: string; texto?: string }) {
  const seguro = urlDoCheckout(url);
  if (!seguro) return null;
  return (
    <a className={classe} href={seguro} target="_blank" rel="noopener noreferrer">
      {texto ?? t('pag.checkout.abrir')}
    </a>
  );
}
